import React, {useEffect, useState} from 'react';
import {Button, Drawer, Input, Space, Spin, Tag, message} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import ImageUploader from '../../../../components/ImageUploader';
import {
    GET_PARTNER_PENALTY_DETAIL,
    GetPartnerPenaltyDetailResponse,
    GetPartnerPenaltyDetailVariables,
} from '../graphql/queries';
import {
    APPEAL_PARTNER_PENALTY,
    AppealPartnerPenaltyResponse,
    AppealPartnerPenaltyVariables,
    SAVE_PARTNER_PENALTY_APPEAL_DRAFT,
    SavePartnerPenaltyAppealDraftResponse,
    SavePartnerPenaltyAppealDraftVariables,
} from '../graphql/mutations';
import {PENALTY_APPEAL_ACTION} from '../graphql/types';
import {canAppeal} from '../utils/penaltyCenterUtils';
import PenaltyBasicInfo, {SectionTitle} from './PenaltyBasicInfo';

const UPLOAD_TAG = 'partner-penalty-appeal';
const MAX_EVIDENCE_COUNT = 9;
/** 与页面提示文案一致；ImageUploader 不传 maxSizeMB 就完全不校验大小，必须显式给 */
const MAX_EVIDENCE_SIZE_MB = 10;
/** 比 ImageUploader 的默认值少一个 gif，与提示文案「支持 jpg / png」对齐 */
const EVIDENCE_ACCEPT = '.jpg,.jpeg,.png';
/** 与后端 PartnerPenaltyCenterMutation.MAX_APPEAL_REASON_LENGTH 及 appeal_reason varchar(256) 对齐 */
const MAX_REASON_LENGTH = 256;

interface PenaltyAppealDrawerProps {
    open: boolean;
    /** 罚单 ID；为空时不发起详情查询 */
    penaltyId: number | null;
    onClose: () => void;
    /** 提交成功回调，由外层刷新列表与 Tab 计数 */
    onSubmitted: () => void;
}

/**
 * 申诉表单抽屉。
 * 打开时拉一次详情，把已保存的草稿（申诉理由 + 证据图）回填到表单；
 * 「保存草稿」只落材料不改状态，「提交」才把罚单推进到审核中。
 */
const PenaltyAppealDrawer: React.FC<PenaltyAppealDrawerProps> = ({
    open,
    penaltyId,
    onClose,
    onSubmitted,
}) => {
    const {t} = useTranslation();
    const [reason, setReason] = useState('');
    const [evidenceKeys, setEvidenceKeys] = useState<string[]>([]);
    // 仅用于回填展示：后端签名 URL 有时效，用户改动后不再跟随 evidenceKeys 变化
    const [prefilledUrls, setPrefilledUrls] = useState<string[]>([]);

    // network-only：草稿可能在别处被改过，重新打开必须拿最新的，不能吃缓存
    const {data, loading: detailLoading} = useQuery<
        GetPartnerPenaltyDetailResponse,
        GetPartnerPenaltyDetailVariables
    >(GET_PARTNER_PENALTY_DETAIL, {
        variables: {id: penaltyId ?? 0},
        skip: !open || !penaltyId,
        fetchPolicy: 'network-only',
        onError: () => message.error(t('partnerControl.penaltyCenter.appeal.loadFailed')),
    });
    const detail = data?.partnerPenaltyDetail;

    const [appealPartnerPenalty, {loading: submitting}] = useMutation<
        AppealPartnerPenaltyResponse,
        AppealPartnerPenaltyVariables
    >(APPEAL_PARTNER_PENALTY);
    const [saveAppealDraft, {loading: savingDraft}] = useMutation<
        SavePartnerPenaltyAppealDraftResponse,
        SavePartnerPenaltyAppealDraftVariables
    >(SAVE_PARTNER_PENALTY_APPEAL_DRAFT);

    // 关闭时清空，避免上一单的申诉材料残留到下一单
    useEffect(() => {
        if (!open) {
            setReason('');
            setEvidenceKeys([]);
            setPrefilledUrls([]);
        }
    }, [open]);

    // 详情到达后回填草稿；keys 与 urls 由后端保证下标一一对应
    useEffect(() => {
        if (!detail) return;
        setReason(detail.appealReason ?? '');
        setEvidenceKeys(detail.evidenceObjectKeys ?? []);
        setPrefilledUrls(detail.evidenceImageUrls ?? []);
    }, [detail]);

    const busy = submitting || savingDraft;
    // 超过 48h 或状态已流转时只读；权威判定在后端，这里只做体验上的拦截
    const editable = detail != null && canAppeal(detail.status, detail.appealDeadlineAt, Date.now());

    /**
     * 校验并产出两个 mutation 共用的申诉材料；不合法时提示并返回 null。
     */
    const buildAppealPayload = (): {appealReason: string; evidenceObjectKeys: string[] | null} | null => {
        const trimmed = reason.trim();
        if (!trimmed) {
            message.warning(t('partnerControl.penaltyCenter.appeal.reasonRequired'));
            return null;
        }
        return {
            appealReason: trimmed,
            evidenceObjectKeys: evidenceKeys.length ? evidenceKeys : null,
        };
    };

    /** 保存草稿：成功后留在抽屉内，方便用户继续补充材料 */
    const handleSaveDraft = async () => {
        const payload = penaltyId ? buildAppealPayload() : null;
        if (!payload) return;
        try {
            await saveAppealDraft({variables: {id: penaltyId as number, ...payload}});
            message.success(t('partnerControl.penaltyCenter.appeal.draftSaved'));
        } catch {
            message.error(t('partnerControl.penaltyCenter.appeal.draftFailed'));
        }
    };

    /** 提交申诉：推进到审核中，成功后关闭抽屉并通知外层刷新 */
    const handleSubmit = async () => {
        const payload = penaltyId ? buildAppealPayload() : null;
        if (!payload) return;
        try {
            await appealPartnerPenalty({
                variables: {id: penaltyId as number, action: PENALTY_APPEAL_ACTION.APPEAL, ...payload},
            });
            message.success(t('partnerControl.penaltyCenter.appeal.submitSuccess'));
            onSubmitted();
            onClose();
        } catch {
            message.error(t('partnerControl.penaltyCenter.appeal.submitFailed'));
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={1100}
            destroyOnClose
            title={
                <Space>
                    <span>{t('partnerControl.penaltyCenter.appeal.title')}</span>
                    {/* 详情到达前 detail 为 undefined，不渲染空 Tag */}
                    {detail?.penaltyNo && <Tag>{detail.penaltyNo}</Tag>}
                </Space>
            }
            footer={
                <div className="flex justify-end">
                    <Space>
                        <Button onClick={onClose} disabled={busy}>
                            {t('partnerControl.penaltyCenter.appeal.cancel')}
                        </Button>
                        <Button
                            loading={savingDraft}
                            disabled={!editable || submitting}
                            onClick={() => void handleSaveDraft()}
                        >
                            {t('partnerControl.penaltyCenter.appeal.saveDraft')}
                        </Button>
                        <Button
                            type="primary"
                            loading={submitting}
                            disabled={!editable || savingDraft}
                            onClick={() => void handleSubmit()}
                        >
                            {t('partnerControl.penaltyCenter.appeal.submit')}
                        </Button>
                    </Space>
                </div>
            }
        >
            <Spin spinning={detailLoading}>
                <SectionTitle
                    title={t('partnerControl.penaltyCenter.detail.basicInfo')}
                    hint={t('partnerControl.penaltyCenter.detail.basicInfoHint')}
                />
                <div className="mb-4">
                    <PenaltyBasicInfo detail={detail} columns={4} />
                </div>

                {detail != null && !editable && (
                    <div className="mb-3 text-red-500">
                        {t('partnerControl.penaltyCenter.appeal.expired')}
                    </div>
                )}

                <SectionTitle
                    title={t('partnerControl.penaltyCenter.appeal.contentSection')}
                    hint={t('partnerControl.penaltyCenter.appeal.contentSectionHint')}
                />

                <div className="mb-2 font-medium">
                    {t('partnerControl.penaltyCenter.appeal.reason')}
                    <span className="text-red-500 ml-1">*</span>
                </div>
                <Input.TextArea
                    rows={5}
                    value={reason}
                    maxLength={MAX_REASON_LENGTH}
                    showCount
                    disabled={busy || !editable}
                    placeholder={t('partnerControl.penaltyCenter.appeal.reasonPlaceholder') as string}
                    onChange={(e) => setReason(e.target.value)}
                />

                <div className="mt-4 mb-1 font-medium">
                    {t('partnerControl.penaltyCenter.appeal.evidence')}
                </div>
                <ImageUploader
                    value={evidenceKeys}
                    displayUrls={prefilledUrls}
                    onChange={setEvidenceKeys}
                    maxCount={MAX_EVIDENCE_COUNT}
                    accept={EVIDENCE_ACCEPT}
                    maxSizeMB={MAX_EVIDENCE_SIZE_MB}
                    tag={UPLOAD_TAG}
                    disabled={busy || !editable}
                />
                <div className="mt-1 text-xs text-gray-500">
                    {t('partnerControl.penaltyCenter.appeal.evidenceHint', {
                        maxSize: MAX_EVIDENCE_SIZE_MB,
                        maxCount: MAX_EVIDENCE_COUNT,
                    })}
                </div>
            </Spin>
        </Drawer>
    );
};

export default PenaltyAppealDrawer;
